import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './Lightbox.css';
import { useLenis } from './SmoothScroll';
import { EASE } from './motion';

// Celoobrazovkový prohlížeč fotek: šipky, Esc, swipe.
// Rodič ho obaluje do <AnimatePresence>, aby fungovala animace zavření.
const Lightbox = ({ images, index, onClose, onChange }) => {
  const { t } = useTranslation();
  const lenis = useLenis();
  const [direction, setDirection] = useState(0);
  const count = images.length;

  const go = (step) => {
    if (count < 2) return;
    setDirection(step);
    onChange((index + step + count) % count);
  };

  useEffect(() => {
    lenis?.stop();
    document.body.style.overflow = 'hidden';
    return () => {
      lenis?.start();
      document.body.style.overflow = '';
    };
  }, [lenis]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const image = images[index];

  return createPortal(
    <motion.div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      onClick={onClose}
    >
      <div className="lightbox__bar" onClick={(e) => e.stopPropagation()}>
        {count > 1 && (
          <span className="lightbox__count">
            {String(index + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
          </span>
        )}
        <button type="button" className="lightbox__close" onClick={onClose} aria-label={t('lightbox.close')}>
          <span />
          <span />
        </button>
      </div>

      <div className="lightbox__stage">
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.img
            key={image.src}
            src={image.src}
            alt={image.alt}
            className="lightbox__img"
            custom={direction}
            variants={{
              enter: (d) => ({ x: d * 80, opacity: 0, scale: 0.96 }),
              center: { x: 0, opacity: 1, scale: 1 },
              exit: (d) => ({ x: d * -80, opacity: 0, scale: 0.96 }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.5, ease: EASE }}
            drag={count > 1 ? 'x' : false}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.6}
            onDragEnd={(_, info) => {
              if (info.offset.x < -80) go(1);
              else if (info.offset.x > 80) go(-1);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </AnimatePresence>
      </div>

      {count > 1 && (
        <>
          <button
            type="button"
            className="lightbox__nav lightbox__nav--prev"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            aria-label={t('lightbox.prev')}
          >
            ←
          </button>
          <button
            type="button"
            className="lightbox__nav lightbox__nav--next"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            aria-label={t('lightbox.next')}
          >
            →
          </button>
        </>
      )}
    </motion.div>,
    document.body
  );
};

export default Lightbox;
